import RpcTypes from 'constants/rpcTypes';
import { AccountingRequestMainMenuItem } from 'pages/accountingStatementRequests/components/MenuItem';

type MenuItemBase = {
  icon: string;
  title: string;
};

type MenuItemWithPath = MenuItemBase & {
  path: string;
  permissions: RpcTypes[];
  customComponent?: React.FC<{
    icon: string;
    title: string;
    selected?: boolean;
    withTooltip?: boolean;
  }>;
  children?: never;
};

type MenuItemWithChildren = MenuItemBase & {
  children: {
    path: string;
    icon: string;
    title: string;
    permissions: RpcTypes[];
  }[];
  path?: never;
  permissions?: never;
  customComponent?: never;
};

export type MenuItem = MenuItemWithPath | MenuItemWithChildren;

export const menuConfiguration: MenuItem[] = [
  {
    path: '/get-started',
    icon: 'Main',
    title: 'mainMenu.getStarted',
    permissions: [RpcTypes.GetStarted_Project],
  },
  {
    path: '/home',
    icon: 'Main',
    title: 'mainMenu.home',
    permissions: [RpcTypes.Home_View],
  },
  {
    path: '/payments',
    icon: 'Transactions',
    title: 'mainMenu.transactions',
    permissions: [RpcTypes.Payments_List],
  },
  {
    path: '/finance',
    icon: 'Finances',
    title: 'mainMenu.finance',
    permissions: [
      RpcTypes.Balance_Get,
      RpcTypes.Balance_RealTime,
      RpcTypes.FinancialReport_ListRequest,
      RpcTypes.BankAccount_List,
      RpcTypes.BalanceTransfer_List,
      RpcTypes.SettlementRequest_List,
    ],
  },
  {
    path: '/analytics',
    icon: 'Analytics',
    title: 'mainMenu.analytics',
    permissions: [RpcTypes.Analytics_Ts],
  },
  {
    icon: 'Payments',
    title: 'mainMenu.payments',
    children: [
      {
        path: '/invoices',
        icon: 'Payment Links',
        title: 'mainMenu.invoices',
        permissions: [RpcTypes.Invoice_List],
      },
      {
        path: '/payouts',
        icon: 'Payouts',
        title: 'mainMenu.payouts',
        permissions: [
          RpcTypes.BulkPayouts_List,
          RpcTypes.PaymentOperation_Payout,
          RpcTypes.BulkPayouts_UploadPayout,
        ],
      },
      {
        path: '/recurring-payments',
        icon: 'Subscriptions',
        title: 'mainMenu.subscription',
        permissions: [RpcTypes.SubscriptionPayment_List],
      },
      {
        path: '/remittance',
        icon: 'Remittances',
        title: 'mainMenu.remittance',
        permissions: [RpcTypes.Remittance_ListRecipientAccounts],
      },
      {
        path: '/manual-payments',
        icon: 'Mass Payments',
        title: 'mainMenu.manualPayments',
        permissions: [
          RpcTypes.BulkPayouts_List,
          RpcTypes.BulkPayouts_UploadRefund,
          RpcTypes.BulkPayouts_UploadCapture,
          RpcTypes.BulkPayouts_UploadCancel,
        ],
      },
      {
        path: '/virtual-terminal-form',
        icon: 'Virtual Terminal',
        title: 'mainMenu.virtualTerminal',
        permissions: [RpcTypes.MotoPayment_Create],
      },
    ],
  },
  {
    path: '/references',
    icon: 'References',
    title: 'mainMenu.references',
    permissions: [RpcTypes.PaymentOperationReference_List],
  },
  {
    path: '/risks',
    icon: 'Risks',
    title: 'mainMenu.risks',
    permissions: [RpcTypes.Rcs_ListBlackWhite, RpcTypes.Rcs_ListFraud],
  },
  {
    path: '/chargebacks',
    icon: 'Chargebacks',
    title: 'mainMenu.chargebacks',
    permissions: [RpcTypes.Chargeback_List],
  },
  {
    path: '/maf',
    icon: 'Business Docs',
    title: 'mainMenu.maf',
    permissions: [RpcTypes.Onboarding_Business_Docs],
  },
  {
    icon: 'Management',
    title: 'mainMenu.management',
    children: [
      {
        path: '/reports',
        icon: 'Reports',
        title: 'mainMenu.reports',
        permissions: [RpcTypes.ReportTemplateTaskHistory_List],
      },
      {
        path: '/projects',
        icon: 'Projects',
        title: 'mainMenu.projects',
        permissions: [RpcTypes.Project_Get],
      },
      {
        path: '/team',
        icon: 'My Teams',
        title: 'mainMenu.team',
        permissions: [RpcTypes.Team_ListUsers],
      },
    ],
  },
  {
    path: '/merchant-onboarding',
    icon: 'Onboarding',
    title: 'mainMenu.merchantOnboarding',
    permissions: [RpcTypes.MerchantRegistration_GetLastFormState],
  },
  {
    path: '/admin-tools',
    icon: 'Admin Tools',
    title: 'mainMenu.adminTools',
    permissions: [
      RpcTypes.Localize_LexemesList,
      RpcTypes.ApsIcon_List,
      RpcTypes.User_List,
      RpcTypes.InternalDomain_List,
    ],
  },
];

export const legacyMenuConfiguration = [
  {
    path: '/get-started',
    icon: 'im-Home',
    title: 'mainMenu.getStarted',
    permissions: [RpcTypes.GetStarted_Project],
  },
  {
    path: '/home',
    icon: 'im-Home',
    title: 'mainMenu.home',
    permissions: [RpcTypes.Home_View],
  },
  {
    path: '/analytics',
    icon: 'im-Analytics',
    title: 'mainMenu.analytics',
    permissions: [RpcTypes.Analytics_Ts],
  },
  {
    path: '/payments',
    icon: 'im-Payments',
    title: 'mainMenu.payments',
    permissions: [RpcTypes.Payments_List],
  },
  {
    path: '/manual-payments',
    icon: 'im-Payout',
    title: 'mainMenu.manualPayments',
    permissions: [
      RpcTypes.PaymentOperation_Payout,
      RpcTypes.BulkPayouts_List,
      RpcTypes.BulkPayouts_UploadPayout,
      RpcTypes.BulkPayouts_UploadRefund,
      RpcTypes.BulkPayouts_UploadCapture,
      RpcTypes.BulkPayouts_UploadCancel,
      RpcTypes.MotoPayment_Create,
    ],
  },
  {
    path: '/remittance',
    icon: 'im-Remmitance',
    title: 'mainMenu.remittance',
    permissions: [RpcTypes.Remittance_ListRecipientAccounts],
  },
  {
    path: '/invoices',
    icon: 'im-Invoice',
    title: 'mainMenu.invoices',
    permissions: [RpcTypes.Invoice_List],
  },
  // TODO: remove in further version if this page not needed
  {
    path: '/accounting-statement-request',
    icon: 'im-Browser',
    title: 'mainMenu.accouningStatementRequest',
    customComponent: AccountingRequestMainMenuItem,
    permissions: [RpcTypes.AccountingDataRequest_List],
  },
  {
    path: '/recurring-payments',
    icon: 'im-Subscription',
    title: 'mainMenu.subscription',
    permissions: [RpcTypes.SubscriptionPayment_List],
  },
  {
    path: '/references',
    icon: 'im-References',
    title: 'mainMenu.references',
    permissions: [RpcTypes.PaymentOperationReference_List],
  },
  {
    path: '/reports',
    icon: 'im-Reports',
    title: 'mainMenu.reports',
    permissions: [RpcTypes.ReportTemplateTaskHistory_List],
  },
  {
    path: '/finance',
    icon: 'im-Finance',
    title: 'mainMenu.finance',
    permissions: [
      RpcTypes.Balance_Get,
      RpcTypes.Balance_RealTime,
      RpcTypes.Balance_Statement,
      RpcTypes.FinancialReport_ListRequest,
      RpcTypes.BankAccount_List,
      RpcTypes.BalanceTransfer_List,
      RpcTypes.SettlementRequest_List,
    ],
  },
  {
    path: '/risks',
    icon: 'im-Risk',
    title: 'mainMenu.risks',
    permissions: [RpcTypes.Rcs_ListBlackWhite, RpcTypes.Rcs_ListFraud],
  },
  {
    path: '/chargebacks',
    icon: 'im-Chargebacks',
    title: 'mainMenu.chargebacks',
    permissions: [RpcTypes.Chargeback_List],
  },
  {
    path: '/projects',
    icon: 'im-Projects',
    title: 'mainMenu.projects',
    permissions: [RpcTypes.Project_Get],
  },
  {
    path: '/maf',
    icon: 'im-Many-Files',
    title: 'mainMenu.maf',
    permissions: [RpcTypes.Onboarding_Business_Docs],
  },
  {
    path: '/merchant-onboarding',
    icon: 'im-Many-Files',
    title: 'mainMenu.merchantOnboarding',
    permissions: [RpcTypes.MerchantRegistration_GetLastFormState],
  },
  {
    path: '/team',
    icon: 'im-My-team',
    title: 'mainMenu.team',
    permissions: [RpcTypes.Team_ListUsers],
  },
    {
    path: '/offline-qr',
    icon: 'im-Camera',
    title: 'mainMenu.offlineQr',
    permissions: [RpcTypes.OfflineQr_List],
  },

  // TODO: remove in further version if this page not needed
  {
    path: '/reconciliation',
    icon: 'im-Reconcilations',
    title: 'mainMenu.reconciliation',
    permissions: [RpcTypes.Reconciliation_Get, RpcTypes.Reconciliation_SetData],
  },
  {
    path: '/settlements',
    title: 'mainMenu.settlementManage',
    icon: 'im-Finance',
    permissions: [RpcTypes.Settlement_List],
  },
  {
    path: '/admin-tools',
    icon: 'im-Configurator-2',
    title: 'mainMenu.adminTools',
    permissions: [
      RpcTypes.Localize_LexemesList,
      RpcTypes.ApsIcon_List,
      RpcTypes.User_List,
      RpcTypes.InternalDomain_List,
      RpcTypes.Feature_List,
    ],
  },
  {
    path: '/actions',
    icon: 'briefCase',
    title: 'mainMenu.actions',
    permissions: [
      RpcTypes.Actions_GetRegister,
    ],
  },
];
